import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Plus, Mail, Phone, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { Modal, Field } from "@/pages/Properties";

const inputCls = "w-full h-10 px-3 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 text-sm";

const empty = { full_name: "", email: "", phone: "", id_number: "", emergency_contact: "", notes: "" };

export default function Tenants() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get("/tenants");
    setItems(data);
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tenants", form);
      toast.success("Tenant added");
      setForm(empty);
      setShowForm(false);
      load();
    } catch { toast.error("Could not save tenant"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this tenant?")) return;
    await api.delete(`/tenants/${id}`);
    toast.success("Tenant deleted");
    load();
  };

  return (
    <Layout
      title="Tenants"
      subtitle="Everyone renting from you, with full contact info"
      actions={
        <button onClick={() => setShowForm(true)} data-testid="add-tenant-btn" className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors">
          <Plus className="w-4 h-4" /> Add tenant
        </button>
      }
    >
      {items.length === 0 ? (
        <div className="card-flat p-10 text-center fade-up">
          <Users className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <div className="font-display text-lg font-semibold">No tenants yet</div>
          <p className="text-sm text-slate-500 mt-1">Add your first tenant to start building contracts.</p>
        </div>
      ) : (
        <div className="card-flat overflow-hidden fade-up">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left px-6 py-3 font-medium">Name</th>
                <th className="text-left px-6 py-3 font-medium">Email</th>
                <th className="text-left px-6 py-3 font-medium">Phone</th>
                <th className="text-left px-6 py-3 font-medium">ID / Passport</th>
                <th className="text-right px-6 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50/50" data-testid={`tenant-row-${t.id}`}>
                  <td className="px-6 py-3.5">
                    <div className="font-medium text-slate-900">{t.full_name}</div>
                    {t.emergency_contact && <div className="text-xs text-slate-500 mt-0.5">SOS: {t.emergency_contact}</div>}
                  </td>
                  <td className="px-6 py-3.5 text-slate-700">
                    {t.email ? <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-slate-400" />{t.email}</span> : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-6 py-3.5 text-slate-700">
                    {t.phone ? <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5 text-slate-400" />{t.phone}</span> : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-6 py-3.5 text-slate-700 font-mono-plex">{t.id_number || <span className="text-slate-400">—</span>}</td>
                  <td className="px-6 py-3.5 text-right">
                    <button onClick={() => del(t.id)} data-testid={`tenant-delete-${t.id}`} className="text-slate-400 hover:text-red-600 transition p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <Modal onClose={() => setShowForm(false)} title="Add tenant">
          <form onSubmit={save} className="space-y-4" data-testid="tenant-form">
            <Field label="Full name">
              <input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="tenant-name-input" className={inputCls} placeholder="Thabo Molefe" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Email">
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="tenant-email-input" className={inputCls} placeholder="thabo@example.com" />
              </Field>
              <Field label="Phone">
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="tenant-phone-input" className={inputCls} placeholder="+27 …" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="ID / Passport">
                <input value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Emergency contact">
                <input value={form.emergency_contact} onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })} className={inputCls} />
              </Field>
            </div>
            <Field label="Notes">
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inputCls} h-24 py-2`} />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50">Cancel</button>
              <button type="submit" data-testid="tenant-save-btn" className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800">Save tenant</button>
            </div>
          </form>
        </Modal>
      )}
    </Layout>
  );
}
